import type {
  TikTokBrandKeywordVideoSerializer,
  TikTokBrandVideoImpactSerializer,
  TikTokKeywordGraphEntityVideoSerializer,
  TikTokKeywordListItemSerializer,
  TikTokOpportunitySerializer,
  TikTokProductLineScoresSerializer,
  TikTokTrendPointSerializer,
} from "@/generated/DjangoTypes";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {useSSE} from "@/hooks/useSSE";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {tiktokVisibilitySelectedCountryAtom} from "@/store/jotaiAtoms";
import type {TInfiniteQueryOptions, TKeywordVideo, TPaginated, TQueryOptions} from "@/store/types";
import {useMutation, useQueries, useQuery, type InfiniteData} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import dayjs from "dayjs";
import {useAtomValue} from "jotai";
import * as RPNInput from "react-phone-number-input";

export type TTiktokVisibilityVideoType = "brand" | "ugc";

export const MAX_KEYWORD_LENGTH = 80;

export const normalizeKeywordText = (value: string): string => value.trim().replace(/\s+/g, " ");

const SCORE_DISPLAY_DECIMALS = 1;

export const VIDEO_TYPE_TRANSLATION_KEYS: Record<TTiktokVisibilityVideoType, string> = {
  brand: "video_type_brand",
  ugc: "video_type_ugc",
};

// Legacy values (paid_ad, paid_partnership) and any unknown future tag collapse to "ugc" — paid
// classification was dropped because heuristics were too noisy; spec: treat unknown as organic UGC.
// TODO: This can be removed if we prune existing data.
export const normalizeVideoType = (value: string): TTiktokVisibilityVideoType => (value === "brand" ? "brand" : "ugc");

export const getTikTokScoreColor = (score: number | null): string => {
  if (score === null) return "text-neutral-grey-dark no-underline";
  if (score >= 60) return "text-success-medium-green";
  if (score >= 30) return "text-warning-medium-yellow";
  return "text-danger-medium-red";
};

export const getKeywordScoreBgColor = (score: number | null): string => {
  if (score === null) return "bg-neutral-grey-light";
  if (score >= 60) return "bg-success-medium-green";
  if (score >= 30) return "bg-warning-medium-yellow";
  return "bg-danger-medium-red";
};

export const getTrendColor = (value: number | null | undefined): string => {
  if (value === null || value === undefined || value === 0) return "text-neutral-grey-dark";
  return value > 0 ? "text-success-medium-green" : "text-danger-medium-red";
};

export const hasKeywordTrendData = (trend?: TikTokTrendPointSerializer[]): boolean => {
  if (!trend) return false;
  const windowEnd = dayjs().subtract(1, "day").endOf("day");
  const windowStart = windowEnd.subtract(31, "day").startOf("day");
  const validPoints = trend.filter(
    point => point.score !== null && dayjs(point.date).isBetween(windowStart, windowEnd),
  );
  return validPoints.length >= 2;
};

export const getKeywordTrendWindow = (trend: TikTokTrendPointSerializer[]): TikTokTrendPointSerializer[] => {
  const windowEnd = dayjs().format("YYYY-MM-DD");
  const windowStart = dayjs(windowEnd).subtract(31, "day").format("YYYY-MM-DD");
  return trend.filter(point => {
    const date = dayjs(point.date).format("YYYY-MM-DD");
    return date >= windowStart && date <= windowEnd;
  });
};

export const formatTrendChange = (change: number | null): string =>
  change === null ? "--" : `${change > 0 ? "+" : ""}${change}`;

export const getCompetitionRanks = ({scores}: {scores: number[]}): number[] => {
  let currentRank = 0;
  let previousScore: number | null = null;

  return scores.map((score, index) => {
    const displayedScore = Number(score.toFixed(SCORE_DISPLAY_DECIMALS));
    if (previousScore === null || displayedScore !== previousScore) {
      currentRank = index + 1;
      previousScore = displayedScore;
    }
    return currentRank;
  });
};

export const hasAnyCompetitionScore = ({scores}: {scores: number[]}): boolean =>
  scores.some(score => Number(score.toFixed(SCORE_DISPLAY_DECIMALS)) > 0);

// TODO: (future improvement) get videos from dedicated endpoints (e.g. per-keyword graph / product line keywords) instead of mapping `brand_videos` off the keyword list response everywhere we need them.
export const toKeywordVideo = (
  video: TikTokBrandKeywordVideoSerializer | TikTokKeywordGraphEntityVideoSerializer,
): TKeywordVideo => ({
  caption: video.description,
  chipLabel: VIDEO_TYPE_TRANSLATION_KEYS[normalizeVideoType(video.video_type)],
  dates: "dates" in video ? video.dates : undefined,
  id: video.tiktok_video_id,
  isRefluencedCampaign: video.video_type === "refluenced",
  publishedAt: video.published_at ?? undefined,
  rank: video.rank ?? 0,
  scoreChange: video.contribution ? Math.round(video.contribution * 10) / 10 : undefined,
  thumbnailUrl: video.cover_url || undefined,
  username: video.author_username,
  videoUrl: video.video_url,
  views: video.play_count,
});

export type TTiktokVisibilityCompetitorRanking = {
  avg_score: number;
  change_30d: number | null;
  competitor_name: string;
  domain: string;
  id: string;
  is_self: boolean;
};

export type TTiktokVisibilityOverview = {
  content_gap_count: number;
  keyword_count: number;
  last_run_at: string | null;
  overall_score: number;
  quick_win_count: number;
  scoring_channel: string | null;
  scoring_in_progress: boolean;
  top_competitors: {change_30d: number | null; domain: string; is_self: boolean; name: string; score: number}[];
  trend: {date: string; score: number | null}[];
};

type TTiktokVisibilityCountries = {
  allowed_country_count: number;
  countries: {country: RPNInput.Country; is_home: boolean; score: number; status: string}[];
  home: RPNInput.Country;
  keyword_limit: number;
  keyword_usage: number;
};

export type TTiktokVisibilityOpportunitiesResponse = {
  opportunities: TikTokOpportunitySerializer[];
};

type TTiktokVisibilityKeywordGraphCompetitor = {
  change_30d: number | null;
  id: string;
  is_self: boolean;
  name: string;
  rank: number;
  score: number;
  trend?: TikTokTrendPointSerializer[];
  videos?: TikTokKeywordGraphEntityVideoSerializer[];
};

type TTiktokVisibilityKeywordGraph = {
  company: {
    change_30d: number | null;
    name: string;
    score: number | null;
    trend: TikTokTrendPointSerializer[];
  };
  competitors: TTiktokVisibilityKeywordGraphCompetitor[];
  keyword: {
    country: string;
    id: string;
    keyword_text: string;
    query: string;
  };
  videos: TikTokBrandKeywordVideoSerializer[];
};

type TTiktokVisibilityAddCountryResponse = {
  channel: string;
  country: RPNInput.Country;
  run_id: string;
  status: string;
};

export type TTiktokVisibilityOnboardingState = {
  has_seen_onboarding: boolean;
  latest_completed_run_id: string | null;
  latest_run_id: string | null;
  latest_run_status: string | null;
  latest_run_type: string | null;
  missing_sse: boolean;
  prefilled_url: string;
  profile_exists: boolean;
};

export type TTiktokVisibilityReplayResponse = TTiktokVisibilityRunReference | {skip: true};

export type TTiktokVisibilityRunReference = {
  channel: string;
  run_id: string;
};

export const GLOBAL_POOL_TAB = "global";

export type TProductLineDraft = {
  competitors: string[];
  id?: string;
  isPendingDeletion: boolean;
  isPrimary: boolean;
  keywords: string[];
  name: string;
};

export type TTiktokVisibilityScoringProgress = {
  competitors?: Record<string, "done" | "loading">;
  countries?: Record<
    string,
    {keywords?: Record<string, Record<string, "done" | "loading">>; status?: "done" | "loading"}
  >;
  current_step?: string;
  done?: boolean;
  keywords?: Record<string, Record<string, "done" | "loading">>;
  progress: number;
  scoring?: boolean;
};

// The global `keywords` map only flips a keyword 'done' once EVERY country
// finished re-scraping it; the dashboard shows one country, so prefer that
// country's own map when the backend sent it — its spinner clears as soon as
// the viewed country is done instead of after the whole multi-country sweep.
const keywordsMapForCountry = (progress: TTiktokVisibilityScoringProgress | undefined, country?: string) =>
  (country ? progress?.countries?.[country]?.keywords : undefined) ?? progress?.keywords;

export const isKeywordScoring = (
  progress: TTiktokVisibilityScoringProgress | undefined,
  productLineName: string,
  keywordText: string,
  country?: string,
): boolean => keywordsMapForCountry(progress, country)?.[productLineName]?.[keywordText] === "loading";

export const isCompetitorScoring = (
  progress: TTiktokVisibilityScoringProgress | undefined,
  competitorName: string,
): boolean => progress?.competitors?.[competitorName] === "loading";

export const isProductLineScoring = (
  progress: TTiktokVisibilityScoringProgress | undefined,
  productLineName: string,
  country?: string,
): boolean =>
  Object.values(keywordsMapForCountry(progress, country)?.[productLineName] ?? {}).some(status => status === "loading");

export const TIKTOK_COUNTRY_SCAN_STEPS = [
  "discovering_keywords",
  "crawling_videos",
  "ranking_competitors",
  "selecting_top_keywords",
] as const;

export type TTiktokCountryScanStep = (typeof TIKTOK_COUNTRY_SCAN_STEPS)[number];

export type TTiktokCountryScanProgress = {
  country_code?: RPNInput.Country;
  country_name?: string;
  current_step?: TTiktokCountryScanStep;
  done?: boolean;
  error?: string;
  progress?: number;
};

export type TTiktokCountryScanVariant = "add" | "switch";

export type TTiktokCountryScanSession = {
  agencyId?: number;
  initialProgress?: number;
  initialStep?: TTiktokCountryScanStep;
  streamId: string;
  targetCountry: RPNInput.Country;
};

export type TTiktokVisibilityLimitOverride = {
  brand_name: string;
  company_profile_id: number;
  country_count: number | null;
  effective_country_count: number;
  effective_keyword_limit: number;
  effective_sync_interval_hours: number;
  effective_tiktok_search: boolean;
  keyword_limit: number | null;
  picture320: string | null;
  sync_interval_hours: number | null;
  tiktok_search: boolean | null;
};

type TTiktokVisibilityCountryProgressState = {
  active: boolean;
  country_code: RPNInput.Country;
  current_step: TTiktokCountryScanStep;
  done: boolean;
  error: string | null;
  progress: number;
  session_id: string;
};

export const TIKTOK_VISIBILITY_KEYS = {
  mAddCountry: ["/tiktok_visibility/countries/add/"],
  mKeyword: ({companyProfileId, keywordId}: {companyProfileId: number; keywordId: string}) => [
    `/tiktok_visibility/keywords/${keywordId}/?company_profile=${companyProfileId}`,
  ],
  mKeywords: ["/tiktok_visibility/keywords/"],
  mLimitOverride: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/admin/limit_overrides/${companyProfileId}/`,
  ],
  mOnboardingDismiss: ["/tiktok_visibility/onboarding/dismiss/"],
  mOnboardingReplay: ["/tiktok_visibility/onboarding/replay/"],
  mOnboardingStart: ["/tiktok_visibility/onboarding/start/"],
  mSwitchCountry: ["/tiktok_visibility/profile/switch_country/"],
  mProductLineBulkAssignments: ({
    companyProfileId,
    country,
  }: {
    companyProfileId: number;
    country?: RPNInput.Country;
  }) => [
    `/tiktok_visibility/product_lines/bulk_assignments/?company_profile=${companyProfileId}${country ? `&country=${country}` : ""}`,
  ],
  qAddCountryStream: ({runId}: {runId: string}) => [`/tiktok_visibility/add_country/${runId}/stream/`],
  qAddState: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/countries/add_state/?company_profile=${companyProfileId}`,
  ],
  qCampaignKeywordSuggestions: ({campaignId, country}: {campaignId: number; country?: RPNInput.Country}) => [
    "/tiktok_visibility/campaign_keyword_suggestions/",
    campaignId,
    country ?? "",
  ],
  qCountries: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/countries/?company_profile=${companyProfileId}`,
  ],
  qCompetitorRanking: ({companyProfileId, country}: {companyProfileId: number; country: RPNInput.Country}) => [
    `/tiktok_visibility/competitors/ranking/?company_profile=${companyProfileId}${`&country=${country}`}`,
  ],
  qKeywordGraph: ({
    companyProfileId,
    country,
    keyword,
  }: {
    companyProfileId: number;
    country: RPNInput.Country;
    keyword: string;
  }) => [
    `/tiktok_visibility/keyword_graph/?company_profile=${companyProfileId}${`&country=${country}`}&keyword=${keyword}`,
  ],
  qKeywords: ({
    companyProfileId,
    country,
    search,
  }: {
    companyProfileId: number;
    country?: RPNInput.Country;
    search?: string;
  }) => [
    `/tiktok_visibility/keywords/?company_profile=${companyProfileId}${country ? `&country=${country}` : ""}${search ? `&search=${search}` : ""}`,
  ],
  qKeywordsInUse: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/keywords/in_use/?company_profile=${companyProfileId}`,
  ],
  qBrandVideos: ({
    companyProfileId,
    country,
    limit,
  }: {
    companyProfileId: number;
    country: RPNInput.Country;
    limit?: number;
  }) => [
    `/tiktok_visibility/brand_videos/?company_profile=${companyProfileId}${`&country=${country}`}${limit ? `&limit=${limit}` : ""}`,
  ],
  qLimitOverrides: ({agencyId}: {agencyId: number}) => [
    `/tiktok_visibility/admin/limit_overrides/?agency_id=${agencyId}`,
  ],
  qOnboardingState: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/onboarding/state/?company_profile=${companyProfileId}`,
  ],
  qOpportunities: ({
    companyProfileId,
    country,
    language,
  }: {
    companyProfileId: number;
    country: RPNInput.Country;
    language: string;
  }) => [
    `/tiktok_visibility/opportunities/?company_profile=${companyProfileId}&language=${language}${`&country=${country}`}`,
  ],
  qOverview: ({companyProfileId, country}: {companyProfileId: number; country: RPNInput.Country}) => [
    `/tiktok_visibility/overview/?company_profile=${companyProfileId}${`&country=${country}`}`,
  ],
  qProductLineAssignments: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/product_lines/assignments/?company_profile=${companyProfileId}`,
  ],
  qProductLineRecommendations: ({
    companyProfileId,
    newProductLine,
  }: {
    companyProfileId: number;
    newProductLine?: string;
  }) => [
    `/tiktok_visibility/product_lines/recommendations/?company_profile=${companyProfileId}${newProductLine ? `&new_product_line=${encodeURIComponent(newProductLine)}` : ""}`,
  ],
  qProductLineScores: ({
    companyProfileId,
    country,
    top3,
  }: {
    companyProfileId: number;
    country: RPNInput.Country;
    top3?: boolean;
  }) => [
    `/tiktok_visibility/product_line_scores/?company_profile=${companyProfileId}${`&country=${country}`}${top3 ? "&top_3=true" : ""}`,
  ],
  qRegion: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/region/?company_profile=${companyProfileId}`,
  ],
  qScoringStream: ({profileUuid}: {profileUuid: string}) => [`/tiktok_visibility/scoring/${profileUuid}/stream/`],
  qSwitchState: ({companyProfileId}: {companyProfileId: number}) => [
    `/tiktok_visibility/profile/switch_state/?company_profile=${companyProfileId}`,
  ],
  qSwitchStream: ({sessionId}: {sessionId: string}) => [`/tiktok_visibility/switch/${sessionId}/stream/`],
} as const;

const invalidateAllScoringQueriesByCountry = (companyProfileId: number, country: RPNInput.Country) => {
  [
    TIKTOK_VISIBILITY_KEYS.qCountries({companyProfileId}),
    TIKTOK_VISIBILITY_KEYS.qCompetitorRanking({companyProfileId, country}),
    TIKTOK_VISIBILITY_KEYS.qOverview({companyProfileId, country}),
    TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({companyProfileId}),
  ].forEach(queryKey => queryClient.invalidateQueries({queryKey, refetchType: "all"}));

  queryClient.invalidateQueries({
    predicate: q =>
      typeof q.queryKey[0] === "string" &&
      ((q.queryKey[0] as string).startsWith("/tiktok_visibility/keywords/") ||
        (q.queryKey[0] as string).startsWith("/tiktok_visibility/product_line_scores/")),
    refetchType: "all",
  });
};

const invalidateGlobalScoringQueries = (companyProfileId: number) => {
  [
    TIKTOK_VISIBILITY_KEYS.qCountries({companyProfileId}),
    TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({companyProfileId}),
  ].forEach(queryKey => queryClient.invalidateQueries({queryKey, refetchType: "all"}));

  queryClient.invalidateQueries({
    predicate: q => {
      const key = q.queryKey[0];
      return (
        typeof key === "string" &&
        (key.startsWith("/tiktok_visibility/keywords/") ||
          key.startsWith("/tiktok_visibility/product_lines/assignments/") ||
          key.startsWith("/tiktok_visibility/product_line_scores/") ||
          key.startsWith("/tiktok_visibility/overview/") ||
          key.startsWith("/tiktok_visibility/competitors/ranking/"))
      );
    },
    refetchType: "all",
  });
};

export const useBulkAssignProductLinesMutation = ({
  companyProfileId,
  country,
  onSuccessCallback,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  onSuccessCallback?: () => void;
}) =>
  useMutation({
    mutationKey: TIKTOK_VISIBILITY_KEYS.mProductLineBulkAssignments({
      companyProfileId: companyProfileId!,
      country,
    }),
    mutationFn: async (body: {
      product_lines: {
        competitors: string[];
        is_primary: boolean;
        keywords: string[];
        name: string;
      }[];
    }) => {
      const response = await client.put(
        TIKTOK_VISIBILITY_KEYS.mProductLineBulkAssignments({companyProfileId: companyProfileId!, country})[0],
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      if (country) invalidateAllScoringQueriesByCountry(companyProfileId!, country);
      else invalidateGlobalScoringQueries(companyProfileId!);
      onSuccessCallback?.();
    },
  });

type TKeywordListCache = InfiniteData<AxiosResponse<TPaginated<TikTokKeywordListItemSerializer>>>;

const updateCachedKeywordLists = (
  updateResults: (results: TikTokKeywordListItemSerializer[], queryKey: string) => TikTokKeywordListItemSerializer[],
) =>
  queryClient
    .getQueriesData<TKeywordListCache>({
      predicate: query =>
        typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith("/tiktok_visibility/keywords/?"),
    })
    .forEach(([queryKey, cache]) => {
      const page = cache?.pages[0];
      if (!cache || !page) return;

      queryClient.setQueryData<TKeywordListCache>(queryKey, {
        ...cache,
        pages: [{...page, data: {...page.data, results: updateResults(page.data.results, queryKey[0] as string)}}],
      });
    });

const addKeywordToCachedLists = (keyword: TikTokKeywordListItemSerializer) =>
  updateCachedKeywordLists((results, queryKey) => (queryKey.includes("&search=") ? results : [...results, keyword]));

const removeKeywordFromCachedLists = (keywordId: string) =>
  updateCachedKeywordLists(results => results.filter(result => result.id !== keywordId));

const shiftCachedKeywordUsage = (companyProfileId: number | null | undefined, delta: number) => {
  if (!companyProfileId) return;

  queryClient.setQueryData<AxiosResponse<TTiktokVisibilityCountries>>(
    TIKTOK_VISIBILITY_KEYS.qCountries({companyProfileId}),
    cache => cache && {...cache, data: {...cache.data, keyword_usage: Math.max(cache.data.keyword_usage + delta, 0)}},
  );
};

export const useAddTiktokVisibilityKeywordMutation = ({
  companyProfileId,
}: {
  companyProfileId: number | null | undefined;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mKeywords,
    mutationFn: async ({keywordText}: {keywordText: string}) => {
      const response = await client.post<TikTokKeywordListItemSerializer>(TIKTOK_VISIBILITY_KEYS.mKeywords[0], {
        company_profile: companyProfileId,
        keyword_text: keywordText,
      });
      return response.data;
    },
    onSuccess: keyword => {
      addKeywordToCachedLists(keyword);
      shiftCachedKeywordUsage(companyProfileId, 1);
    },
  });

export const useDeleteTiktokVisibilityKeywordMutation = ({
  companyProfileId,
}: {
  companyProfileId: number | null | undefined;
}) =>
  useMutation({
    mutationFn: async ({keywordId}: {keywordId: string}) => {
      await client.delete(TIKTOK_VISIBILITY_KEYS.mKeyword({companyProfileId: companyProfileId!, keywordId})[0]);
      return keywordId;
    },
    onSuccess: keywordId => {
      removeKeywordFromCachedLists(keywordId);
      shiftCachedKeywordUsage(companyProfileId, -1);
    },
  });

export type TTiktokKeywordSelection = {
  existing_keyword_ids: string[];
  new_keywords: string[];
};

export type TTiktokKeywordPlanRead = TTiktokKeywordSelection & {
  country?: RPNInput.Country | "";
  is_initialized?: boolean;
  tracked_keywords: {id: string; keyword_text: string}[];
  website_url?: string;
};

type TCampaignKeywordSuggestions = {
  results: {
    state: "no_profile" | "tracking";
    suggestions: {
      existing: {id: string; text: string}[];
      new: {product_line: string; text: string}[];
    };
  };
};

export const useCampaignKeywordSuggestionsQuery = ({
  campaignId,
  country,
  queryOptions,
}: {
  campaignId?: number | null;
  country?: RPNInput.Country;
  queryOptions?: TQueryOptions<TCampaignKeywordSuggestions>;
}) =>
  useGenericQuery<TCampaignKeywordSuggestions>({
    queryKey: TIKTOK_VISIBILITY_KEYS.qCampaignKeywordSuggestions({campaignId: campaignId!}),
    queryFn: () =>
      client.post("/tiktok_visibility/campaign_keyword_suggestions/", {
        campaign_id: campaignId,
        ...(country ? {country} : {}),
      }),
    staleTime: Infinity,
    ...queryOptions,
    enabled: (queryOptions?.enabled ?? true) && !!campaignId,
  });

export const useTiktokVisibilityRegionQuery = ({
  companyProfileId,
  enabled = true,
}: {
  companyProfileId?: number | null;
  enabled?: boolean;
}) =>
  useGenericQuery<{
    region: RPNInput.Country;
    website_url: string | null;
  }>({
    enabled: enabled && !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qRegion({companyProfileId: companyProfileId!}),
  });

export const useTiktokVisibilityKeywordsInUseQuery = ({
  companyProfileId,
  enabled = true,
}: {
  companyProfileId?: number | null;
  enabled?: boolean;
}) =>
  useGenericQuery<{
    locked_keywords: {id: string; keyword_text: string}[];
  }>({
    enabled: enabled && !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qKeywordsInUse({companyProfileId: companyProfileId!}),
  });

export const useSelectedTiktokVisibilityCountry = ({companyProfileId}: {companyProfileId?: number | null}) => {
  const selectedByCompany = useAtomValue(tiktokVisibilitySelectedCountryAtom);
  const selectedCountry = companyProfileId ? selectedByCompany?.[companyProfileId] : undefined;
  const {data: countriesData} = useTiktokVisibilityCountriesQuery({companyProfileId});

  if (selectedCountry && (!countriesData || countriesData.countries.some(c => c.country === selectedCountry))) {
    return selectedCountry;
  }

  return countriesData?.home;
};

export const useTiktokVisibilityCompetitorRankingQuery = ({
  companyProfileId,
  country: countryOverride,
  enabled = true,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  enabled?: boolean;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericInfiniteQuery<TTiktokVisibilityCompetitorRanking>({
    enabled: enabled && !!companyProfileId && !!country,
    queryKey: TIKTOK_VISIBILITY_KEYS.qCompetitorRanking({companyProfileId: companyProfileId!, country: country!}),
  });
};

export const useTiktokVisibilityKeywordsQuery = ({
  companyProfileId,
  country: countryOverride,
  globalPool,
  queryOptions,
  search,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  globalPool?: boolean;
  queryOptions?: TInfiniteQueryOptions<TikTokKeywordListItemSerializer>;
  search?: string;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = globalPool ? undefined : (countryOverride ?? selectedCountry);

  return useGenericInfiniteQuery<TikTokKeywordListItemSerializer>({
    queryKey: TIKTOK_VISIBILITY_KEYS.qKeywords({companyProfileId: companyProfileId!, country, search}),
    ...queryOptions,
    enabled: (queryOptions?.enabled ?? true) && !!companyProfileId && (globalPool || !!country),
  });
};

export const useTiktokVisibilityKeywordGraphQuery = ({
  companyProfileId,
  country: countryOverride,
  keyword,
  queryOptions,
}: {
  companyProfileId?: number | null;
  country?: RPNInput.Country;
  keyword?: string | null;
  queryOptions?: TQueryOptions<TTiktokVisibilityKeywordGraph>;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericQuery<TTiktokVisibilityKeywordGraph>({
    queryKey: TIKTOK_VISIBILITY_KEYS.qKeywordGraph({
      companyProfileId: companyProfileId!,
      country: country!,
      keyword: keyword!,
    }),
    ...queryOptions,
    enabled: (queryOptions?.enabled ?? true) && !!companyProfileId && !!country && !!keyword,
  });
};

export const useTiktokVisibilityProductLineScoresQuery = ({
  companyProfileId,
  country: countryOverride,
  queryOptions,
  top3,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  queryOptions?: TQueryOptions<TikTokProductLineScoresSerializer>;
  top3?: boolean;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericQuery<TikTokProductLineScoresSerializer>({
    queryKey: TIKTOK_VISIBILITY_KEYS.qProductLineScores({companyProfileId: companyProfileId!, country: country!, top3}),
    ...queryOptions,
    enabled: (queryOptions?.enabled ?? true) && !!companyProfileId && !!country,
  });
};

export const useTiktokVisibilityProductLineAssignmentsQuery = ({
  companyProfileId,
}: {
  companyProfileId?: number | null;
}) =>
  useGenericQuery<{
    product_lines: {
      competitor_count: number;
      competitors: {competitor_name: string; id: string}[];
      id: string;
      is_primary: boolean;
      keyword_count: number;
      keywords: {id: string; keyword_text: string}[];
      name: string;
    }[];
  }>({
    enabled: !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qProductLineAssignments({companyProfileId: companyProfileId!}),
  });

type TProductLineRecommendations = {
  product_lines: {
    competitors: {domain: string; name: string}[];
    id: string;
    keywords: {text: string}[];
    name: string;
  }[];
};

export const useTiktokVisibilityProductLineRecommendationsQuery = ({
  companyProfileId,
}: {
  companyProfileId?: number | null;
}) =>
  useGenericQuery<TProductLineRecommendations>({
    enabled: !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({companyProfileId: companyProfileId!}),
    queryFn: () =>
      client.post(TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({companyProfileId: companyProfileId!})[0]),
  });

export const useTiktokVisibilityNewProductLineRecommendationsQueries = ({
  companyProfileId,
  names,
}: {
  companyProfileId?: number | null;
  names: string[];
}) =>
  useQueries({
    queries: names.map(name => ({
      enabled: !!companyProfileId,
      queryKey: TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({
        companyProfileId: companyProfileId!,
        newProductLine: name,
      }),
      queryFn: () =>
        client.post<TProductLineRecommendations>(
          TIKTOK_VISIBILITY_KEYS.qProductLineRecommendations({
            companyProfileId: companyProfileId!,
            newProductLine: name,
          })[0],
        ),
      select: (response: AxiosResponse<TProductLineRecommendations>) => response.data,
    })),
  });

export const useTiktokVisibilityBrandVideosQuery = ({
  companyProfileId,
  country: countryOverride,
  limit,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  limit?: number;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericQuery<TikTokBrandVideoImpactSerializer[]>({
    enabled: !!companyProfileId && !!country,
    queryKey: TIKTOK_VISIBILITY_KEYS.qBrandVideos({companyProfileId: companyProfileId!, country: country!, limit}),
  });
};

export const useTiktokVisibilityOpportunitiesQuery = ({
  companyProfileId,
  country: countryOverride,
  enabled = true,
  language,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  enabled?: boolean;
  language: string;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericQuery<TTiktokVisibilityOpportunitiesResponse>({
    enabled: enabled && !!companyProfileId && !!country,
    queryKey: TIKTOK_VISIBILITY_KEYS.qOpportunities({companyProfileId: companyProfileId!, country: country!, language}),
  });
};

export const useTiktokVisibilityOverviewQuery = ({
  companyProfileId,
  country: countryOverride,
  enabled = true,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  enabled?: boolean;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;

  return useGenericQuery<TTiktokVisibilityOverview>({
    enabled: enabled && !!companyProfileId && !!country,
    queryKey: TIKTOK_VISIBILITY_KEYS.qOverview({companyProfileId: companyProfileId!, country: country!}),
  });
};

export const tiktokCountryScanStreamKey = (variant: TTiktokCountryScanVariant, streamId: string) =>
  variant === "add"
    ? TIKTOK_VISIBILITY_KEYS.qAddCountryStream({runId: streamId})
    : TIKTOK_VISIBILITY_KEYS.qSwitchStream({sessionId: streamId});

export const useTiktokCountryScanStateQuery = ({
  companyProfileId,
  enabled = true,
  variant,
}: {
  companyProfileId: number | null | undefined;
  enabled?: boolean;
  variant: TTiktokCountryScanVariant;
}) =>
  useGenericQuery<TTiktokVisibilityCountryProgressState>({
    enabled: enabled && !!companyProfileId,
    queryKey: (variant === "add" ? TIKTOK_VISIBILITY_KEYS.qAddState : TIKTOK_VISIBILITY_KEYS.qSwitchState)({
      companyProfileId: companyProfileId!,
    }),
    retry: false,
  });

export const useTiktokOnboardingStateQuery = ({
  companyProfileId,
  enabled = true,
}: {
  companyProfileId: number | null | undefined;
  enabled?: boolean;
}) =>
  useGenericQuery<TTiktokVisibilityOnboardingState>({
    enabled: enabled && !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qOnboardingState({companyProfileId: companyProfileId!}),
    retry: (failureCount, error) => {
      if ((error as AxiosError | null)?.response?.status === 403) return false;
      return failureCount < 3;
    },
  });

export const invalidateTiktokVisibilityQueries = () =>
  queryClient.resetQueries({
    predicate: q =>
      typeof q.queryKey[0] === "string" &&
      (q.queryKey[0] as string).startsWith("/tiktok_visibility/") &&
      !(q.queryKey[0] as string).startsWith("/tiktok_visibility/switch/") &&
      !(q.queryKey[0] as string).startsWith("/tiktok_visibility/add_country/"),
  });

export const useAddCountryMutation = ({companyProfileId}: {companyProfileId?: number | null}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mAddCountry,
    mutationFn: async (country: RPNInput.Country) => {
      if (!companyProfileId) throw new Error("missing_company_profile");
      const response = await client.post<TTiktokVisibilityAddCountryResponse>(TIKTOK_VISIBILITY_KEYS.mAddCountry[0], {
        company_profile: companyProfileId,
        country,
      });
      return response.data;
    },
  });

export const useTiktokSwitchCountryMutation = ({companyProfileId}: {companyProfileId?: number | null}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mSwitchCountry,
    mutationFn: async ({region}: {region: RPNInput.Country}) => {
      const response = await client.post<{
        channel: string;
        session_id: string;
      }>(TIKTOK_VISIBILITY_KEYS.mSwitchCountry[0], {
        company_profile: companyProfileId,
        region,
      });

      return response.data;
    },
  });

export const useStartTiktokOnboardingMutation = ({companyProfileId}: {companyProfileId: number | null | undefined}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mOnboardingStart,
    mutationFn: async ({region, websiteUrl}: {region: string; websiteUrl?: string}) => {
      const response = await client.post<TTiktokVisibilityRunReference>(TIKTOK_VISIBILITY_KEYS.mOnboardingStart[0], {
        company_profile: companyProfileId,
        region,
        ...(websiteUrl ? {website_url: websiteUrl} : {}),
      });
      return response.data;
    },
  });

export const useReplayTiktokOnboardingMutation = ({companyProfileId}: {companyProfileId: number | null | undefined}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mOnboardingReplay,
    mutationFn: async () => {
      const response = await client.post<TTiktokVisibilityReplayResponse>(TIKTOK_VISIBILITY_KEYS.mOnboardingReplay[0], {
        company_profile: companyProfileId,
      });
      return response.data;
    },
  });

export const useDismissTiktokOnboardingMutation = ({companyProfileId}: {companyProfileId: number | null | undefined}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: TIKTOK_VISIBILITY_KEYS.mOnboardingDismiss,
    mutationFn: async () => {
      await client.post(TIKTOK_VISIBILITY_KEYS.mOnboardingDismiss[0], {company_profile: companyProfileId});
    },
    onSuccess: () => {
      if (companyProfileId) {
        queryClient.invalidateQueries({
          queryKey: TIKTOK_VISIBILITY_KEYS.qOnboardingState({companyProfileId}),
        });
      }
    },
  });

export const useTiktokVisibilityCountriesQuery = ({
  companyProfileId,
  enabled = true,
}: {
  companyProfileId: number | null | undefined;
  enabled?: boolean;
}) =>
  useGenericQuery<TTiktokVisibilityCountries>({
    enabled: enabled && !!companyProfileId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qCountries({companyProfileId: companyProfileId!}),
  });

export const useTiktokVisibilityLimitOverridesQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number | null | undefined;
  queryOptions?: TQueryOptions<{results: TTiktokVisibilityLimitOverride[]}>;
}) =>
  useGenericQuery<{results: TTiktokVisibilityLimitOverride[]}>({
    enabled: !!agencyId,
    queryKey: TIKTOK_VISIBILITY_KEYS.qLimitOverrides({agencyId: agencyId!}),
    ...queryOptions,
  });

export const useSetTiktokVisibilityLimitOverrideMutation = ({agencyId}: {agencyId: number}) =>
  useMutation({
    mutationFn: async ({
      companyProfileId,
      countryCount,
      keywordLimit,
      syncIntervalHours,
      tiktokSearch,
    }: {
      companyProfileId: number;
      countryCount: number | null;
      keywordLimit: number | null;
      syncIntervalHours: number | null;
      tiktokSearch: boolean;
    }) => {
      const response = await client.put<TTiktokVisibilityLimitOverride>(
        TIKTOK_VISIBILITY_KEYS.mLimitOverride({companyProfileId})[0],
        {
          country_count: countryCount,
          keyword_limit: keywordLimit,
          sync_interval_hours: syncIntervalHours,
          tiktok_search: tiktokSearch,
        },
      );
      return response.data;
    },
    onSuccess: (_data, {companyProfileId}) => {
      [
        TIKTOK_VISIBILITY_KEYS.qLimitOverrides({agencyId}),
        TIKTOK_VISIBILITY_KEYS.qCountries({companyProfileId}),
      ].forEach(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });

export const useTiktokVisibilityScoringStream = ({
  companyProfileId,
  country: countryOverride,
  scoringChannel,
  scoringInProgress,
}: {
  companyProfileId: number | null | undefined;
  country?: RPNInput.Country;
  scoringChannel: string | null;
  scoringInProgress: boolean;
}) => {
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const country = countryOverride ?? selectedCountry;
  const profileUuid = scoringChannel ?? null;
  const enabled = scoringInProgress && !!profileUuid;

  return useSSE({
    enabled,
    initialState: {},
    queryKey: profileUuid ? TIKTOK_VISIBILITY_KEYS.qScoringStream({profileUuid}) : ["__scoring_stream_disabled__"],
    onSuccess: () => {
      if (companyProfileId && country) {
        invalidateAllScoringQueriesByCountry(companyProfileId, country);
      }
    },
  });
};

// Read-only observer of the scoring SSE payload. The single EventSource is opened by
// useTiktokVisibilityScoringStream (mounted in TiktokSearchSummary); this hook just subscribes
// to the same query cache key so sibling components can show per-keyword/competitor loading state
// without opening a second connection.
export const useTiktokVisibilityScoringProgress = ({
  scoringChannel,
  scoringInProgress,
}: {
  scoringChannel: string | null | undefined;
  scoringInProgress: boolean;
}) => {
  const profileUuid = scoringChannel ?? null;
  const isScoring = scoringInProgress && !!profileUuid;

  const {data} = useQuery<TTiktokVisibilityScoringProgress>({
    enabled: false,
    queryKey: profileUuid ? TIKTOK_VISIBILITY_KEYS.qScoringStream({profileUuid}) : ["__scoring_stream_disabled__"],
  });

  return {isScoring, progress: isScoring ? data : undefined};
};
